/**
 * The language provider.
 *
 * Its own file so fast refresh keeps working: a module that exports both a
 * component and a hook is reloaded wholesale on every edit, which drops the
 * state of everything under it.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import {
  DICTIONARIES,
  LanguageContext,
  STORAGE_KEY,
  initialLanguage,
  type Language,
  type LanguageValue,
} from ".";

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(initialLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private browsing. The choice lasts the session, which is enough.
    }
  }, []);

  const value = useMemo<LanguageValue>(() => {
    const dictionary = DICTIONARIES[language];
    return {
      language,
      setLanguage,
      t: (source: string) => dictionary[source] ?? source,
    };
  }, [language, setLanguage]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
